/**
 * Personalization-lite — our self-hosted version of Algolia's plan-gated
 * AI Personalization, at the "boost what this shopper has shown interest in"
 * level of fidelity.
 *
 * A per-browser affinity profile accumulates in localStorage from product
 * page views and collection filter usage. Collection and search queries then
 * apply the strongest fresh affinities as Algolia optionalFilters — soft
 * boosts that rank matching products higher without excluding anything.
 *
 * Only active when the layout renders <meta name="search-personalization">
 * (SEARCH_PERSONALIZATION env var), so environments opt in separately and
 * the whole feature is one config var away from off.
 */
import { getPageLang } from './utils';

/** attribute → value → { n: interaction count, t: last-seen epoch ms } */
type AffinityMap = Record<string, Record<string, { n: number; t: number }>>;

const STORAGE_KEY = 'raffi-search-affinities';
const MAX_VALUES_PER_ATTRIBUTE = 8;
const MIN_INTERACTIONS_FOR_BOOST = 2; // ignore one-off signals
const MAX_BOOSTED_FILTERS = 4; // keep the boost surface small
const DECAY_DAYS = 60; // forget stale interests

export function personalizationEnabled(): boolean {
  return !!document.querySelector('meta[name="search-personalization"]');
}

/**
 * The named-tag attribute for a filter key, e.g. brand → named_tags.brand.
 * Catalog tagging is inconsistent (most products use the plain attribute, a
 * few use a locale-prefixed one like EN_brand) — affinities are stored under
 * the plain attribute, and getOptionalFilters() emits boosts for both.
 */
export function namedTagAttribute(key: string): string {
  return `named_tags.${key}`;
}

function load(): AffinityMap {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function save(map: AffinityMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Storage unavailable (private mode) — personalization silently off
  }
}

export function recordAffinity(attribute: string, value: string | undefined | null): void {
  if (!personalizationEnabled()) return;
  // Values containing filter syntax would corrupt optionalFilters — skip them
  if (!attribute || !value || value.includes(':') || value.includes('<')) return;

  const map = load();
  const values = (map[attribute] = map[attribute] ?? {});
  const entry = (values[value] = values[value] ?? { n: 0, t: 0 });
  entry.n += 1;
  entry.t = Date.now();

  // Cap stored values per attribute — drop the weakest when over
  const keys = Object.keys(values);
  if (keys.length > MAX_VALUES_PER_ATTRIBUTE) {
    const weakest = keys.sort((a, b) => values[a].n - values[b].n)[0];
    delete values[weakest];
  }

  save(map);
}

/**
 * Algolia optionalFilters built from the strongest fresh affinities.
 * Scores cap at 3 so textual relevance still dominates for typed queries.
 */
export function getOptionalFilters(): string[] {
  if (!personalizationEnabled()) return [];

  const cutoff = Date.now() - DECAY_DAYS * 24 * 60 * 60 * 1000;
  const scored: { filter: string; n: number }[] = [];

  const localePrefix = getPageLang().toUpperCase();

  for (const [attribute, values] of Object.entries(load())) {
    for (const [value, { n, t }] of Object.entries(values)) {
      if (n >= MIN_INTERACTIONS_FOR_BOOST && t >= cutoff) {
        const score = Math.min(3, Math.floor(Math.log2(n)) + 1);
        scored.push({ filter: `${attribute}:${value}<score=${score}>`, n });
        // Also cover the locale-prefixed tagging variant (EN_brand etc.)
        if (attribute.startsWith('named_tags.')) {
          const key = attribute.slice('named_tags.'.length);
          scored.push({ filter: `named_tags.${localePrefix}_${key}:${value}<score=${score}>`, n });
        }
      }
    }
  }

  return scored
    .sort((a, b) => b.n - a.n)
    .slice(0, MAX_BOOSTED_FILTERS)
    .map((s) => s.filter);
}

// Product page views are an affinity signal: only the PDP wrapper carries
// data-product-type (collection cards don't), so the selector is specific.
document.addEventListener('DOMContentLoaded', () => {
  const pdp = document.querySelector<HTMLElement>('.js-product[data-product-type]');
  if (!pdp) return;
  recordAffinity('product_type', pdp.dataset.productType);
  recordAffinity(namedTagAttribute('brand'), pdp.dataset.brand);
});
